import { html, render } from 'lit-html'
import { register } from '@polleverywhere/hmr-custom-element'
import './style.scss'

function handleClick (e) {
  e.stopPropagation()

  const selector = this.getAttribute('selector') || 'main'

  // Prioritize moving focus to `main` before other elements.
  const els = Array.from(document.querySelectorAll(selector))
  if (!els.length) { return }

  let focusEl = els.find((el) => el.tagName === 'MAIN')
  if (!focusEl) {
    // When main element doesn't exist, try focusing on the first element.
    focusEl = els[0]
  }

  focusEl.tabIndex = -1
  focusEl.focus()
}

function template (label) {
  return html`<button @click=${handleClick}>${label}</button>`
}

function renderTemplate (el) {
  if (!el.isConnected) return

  const label = el.getAttribute('label') || 'Skip to main content'

  render(template(label), el, {
    eventContext: el
  })

  if (el.hasAttribute('autofocus')) {
    const button = el.querySelector('button')
    button.focus()
    button.classList.add('focus-visible')
  }
}

/**
 * An element for accessibility where it is hidden by default but when focused by tabbing
 * to it will appear from off screen. Clicking on it will programmatically change the focus
 * to the selector provided in the selector attribute. If more than 1 element is found,
 * it will focus on the first available element.
 *
 * It's recommended to put this element as the first element in your document so that this
 * is the first element that is focused on when tabbing.
 *
 * @element skip-to-content
 *
 * @attr {String} [selector='main'] single or comma separated list of selectors for content to skip to
 * @attr {String} [label='Skip to main content'] label to display
 * @attr {Boolean} autofocus Auto focus this current element on initial render
 **/
export default class SkipToContent extends HTMLElement {
  static get tagName () { return 'skip-to-content' }
  static get observedAttributes () { return ['selector', 'label', 'autofocus'] }

  connectedCallback () {
    renderTemplate(this)
  }

  attributeChangedCallback () {
    renderTemplate(this)
  }
}

register(module, SkipToContent.tagName, SkipToContent)
